import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import Seo from '@/components/Seo';
import { applyBrand, resetBrand } from '@/lib/brand';
import { HardHat, Loader2, Images, Film } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PortfolioPublicPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/portfolio/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (data?.brand_color) applyBrand(data.brand_color);
    return () => resetBrand();
  }, [data]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400 font-mono"><Loader2 className="animate-spin mr-2" /> Loading…</div>;

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center" data-testid="portfolio-error">
        <Images size={40} className="text-slate-300 mb-3" />
        <h1 className="font-display font-semibold text-2xl uppercase tracking-tight">Portfolio unavailable</h1>
        <p className="text-slate-500 mt-2">This link has expired or sharing was turned off.</p>
        <Link to="/" className="tm-btn inline-flex mt-5">Go to TradesMate</Link>
      </div>
    );
  }

  const videos = data.items.filter((it) => it.is_video);
  const photos = data.items.filter((it) => !it.is_video);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portfolio-public-page">
      <Seo title={`${data.business_name} — Our Work`} noindex />
      <header className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 py-6 flex items-center gap-3">
          {data.logo_data
            ? <img src={data.logo_data} alt="" className="h-12 w-auto bg-white rounded p-1" />
            : <span className="inline-flex items-center gap-2 font-display font-semibold text-lg text-slate-900"><span className="w-7 h-7 rounded-full bg-brand text-brand-ink grid place-items-center"><HardHat size={14} /></span> TradesMate</span>}
          <div>
            <h1 className="font-display font-semibold text-2xl sm:text-3xl uppercase tracking-tight">{data.business_name}</h1>
            <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Our recent work</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {data.items.length === 0 ? (
          <p className="text-center text-slate-500 py-16">No work has been added yet — check back soon.</p>
        ) : (
          <>
            {videos.length > 0 && (
              <section className="mb-10" data-testid="portfolio-walkthroughs">
                <h2 className="font-display font-semibold text-lg uppercase tracking-tight flex items-center gap-2 mb-4">
                  <Film size={18} className="text-brand" /> Walkthroughs
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {videos.map((it) => (
                    <figure key={it.id} className="tm-card overflow-hidden" data-testid={`portfolio-video-${it.id}`}>
                      <video
                        src={`${API}${it.url}`}
                        controls
                        muted
                        playsInline
                        preload="metadata"
                        className="w-full aspect-video object-cover bg-slate-900"
                      />
                      {it.caption && <figcaption className="p-3 text-sm text-slate-600 whitespace-pre-wrap">{it.caption}</figcaption>}
                    </figure>
                  ))}
                </div>
              </section>
            )}

            {photos.length > 0 && (
              <section data-testid="portfolio-photos">
                {videos.length > 0 && (
                  <h2 className="font-display font-semibold text-lg uppercase tracking-tight flex items-center gap-2 mb-4">
                    <Images size={18} className="text-brand" /> Photos
                  </h2>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {photos.map((it) => (
                    <figure key={it.id} className="tm-card overflow-hidden" data-testid={`portfolio-item-${it.id}`}>
                      <img src={`${API}${it.url}`} alt="Before and after" loading="lazy" className="w-full aspect-square object-cover bg-slate-100" />
                      {it.caption && <figcaption className="p-3 text-sm text-slate-600 whitespace-pre-wrap">{it.caption}</figcaption>}
                    </figure>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        <div className="text-center mt-10">
          <p className="text-slate-500 text-sm">Portfolio powered by <Link to="/" className="text-brand font-bold hover:underline">TradesMate</Link> — free tools for UK tradesmen.</p>
        </div>
      </main>
    </div>
  );
}
